/**
 * AT Command Safety Policy — parser + matcher.
 *
 * Parses a raw AT command line into a structured form and evaluates rules
 * against the structure — never against raw strings — to avoid regex
 * fragility and bypass via whitespace / case / Unicode / semicolon chaining.
 *
 * Fail-closed: if the command doesn't match the accepted grammar, it is
 * BLOCKED. The cost is that a legit exotic form requires adding support to
 * the grammar; the benefit is that an attacker can't smuggle a dangerous
 * command through a parser gap.
 *
 * The rule lists (BLOCKED_RULES, EXEMPT_RULES) are generated from the
 * canonical at-policy.yaml by tools/gen-at-policy.mjs; they're imported
 * here. The parser + matcher logic in this file is hand-written per
 * language (TS here, Python in edge/modem-gateway/app/at_policy.py) and
 * cross-verified by the shared corpus at tests/at-corpus.json.
 *
 * See plans/at-command-safety-layer.md for the full design.
 */
import { AT_POLICY_VERSION, BLOCKED_RULES, EXEMPT_RULES } from './atPolicyRules';
export { AT_POLICY_VERSION };
/**
 * Parse a single AT command line (no semicolon chaining at this layer —
 * callers handle chaining before calling this).
 *
 * Returns null for malformed input. Callers treat null as BLOCKED.
 */
export function parseAtCommand(raw) {
    if (typeof raw !== 'string')
        return null;
    // Trim terminators and outer whitespace.
    let s = raw.replace(/[\r\n]+/g, '').trim();
    if (s.length === 0)
        return null;
    // Case-normalize the AT prefix. Quoted argument contents are preserved.
    // We walk the string manually to keep quoted regions untouched.
    s = normalizeOutsideQuotes(s);
    // Must start with AT (already uppercased by normalization).
    if (!s.startsWith('AT'))
        return null;
    let rest = s.slice(2);
    // Empty command: `AT` alone — accepted as execute with no name (ping).
    if (rest.length === 0) {
        return { prefix: '', name: '', operator: '', args: [] };
    }
    // Prefix dispatch.
    const head = rest.charAt(0);
    if (head === '+') {
        return parseExtended('+', rest.slice(1));
    }
    if (head === '&') {
        return parseAmpersand(rest.slice(1));
    }
    if (head === '$') {
        return parseExtended('$', rest.slice(1));
    }
    // Basic V.250 — starts with a letter (possibly followed by a digit).
    // `ATE0`, `ATI`, `ATZ`, `ATA`, `ATH`, `ATD...`
    if (/^[A-Z]/.test(head)) {
        if (head === 'D') {
            // Dial — opaque body (digits + DTMF chars).
            const body = rest.slice(1);
            if (!/^[0-9A-D*#+,;WTP]*$/.test(body))
                return null;
            return { prefix: 'D', name: '', operator: '', args: [], rawDialString: body };
        }
        const m = /^([A-Z])([0-9]?)$/.exec(rest);
        if (!m)
            return null;
        return { prefix: '', name: m[1] + (m[2] || ''), operator: '', args: [] };
    }
    return null;
}
function parseExtended(prefix, rest) {
    // rest looks like: NAME[op[args]]
    const nameMatch = /^([A-Z][A-Z0-9_]*)/.exec(rest);
    if (!nameMatch)
        return null;
    const name = nameMatch[1];
    let tail = rest.slice(name.length);
    let operator = '';
    if (tail.startsWith('=?')) {
        operator = '=?';
        tail = tail.slice(2);
        if (tail.length !== 0)
            return null;
        return { prefix, name, operator, args: [] };
    }
    if (tail.startsWith('?')) {
        operator = '?';
        tail = tail.slice(1);
        if (tail.length !== 0)
            return null;
        return { prefix, name, operator, args: [] };
    }
    if (tail.startsWith('=')) {
        operator = '=';
        tail = tail.slice(1);
        const args = parseArgs(tail);
        if (args === null)
            return null;
        // Reject `AT+QCFG=` with zero args — semantically empty write form.
        // Fail-closed prevents parser accepting malformed inputs as PASS.
        if (args.length === 0)
            return null;
        return { prefix, name, operator, args };
    }
    if (tail.length === 0) {
        // Execute form, no operator.
        return { prefix, name, operator: '', args: [] };
    }
    return null;
}
function parseAmpersand(rest) {
    // `AT&F`, `AT&W`, `AT&V`, `AT&V0`
    const m = /^([A-Z])([0-9]?)$/.exec(rest);
    if (!m)
        return null;
    return { prefix: '&', name: m[1] + (m[2] || ''), operator: '', args: [] };
}
function parseArgs(raw) {
    // Comma-separated. Outside quotes: whitespace allowed and ignored, commas
    // separate args, and bare tokens are [A-Z0-9_.+-\/]* — characters commonly
    // seen in hex (0x...), decimals, identifiers, paths, and MCC/MNC.
    const out = [];
    let i = 0;
    const skipWs = () => {
        while (i < raw.length && (raw[i] === ' ' || raw[i] === '\t'))
            i++;
    };
    // Empty args list valid: `AT+QCFG=` with nothing after '=' is a user error
    // but we still parse it as zero args. Policy will decide whether that's
    // acceptable.
    skipWs();
    if (i >= raw.length)
        return out;
    while (i < raw.length) {
        skipWs();
        if (raw[i] === '"') {
            // Quoted string. `\"` is the only recognized escape.
            i++;
            let v = '';
            let closed = false;
            while (i < raw.length) {
                const ch = raw[i];
                if (ch === '\\' && raw[i + 1] === '"') {
                    v += '"';
                    i += 2;
                    continue;
                }
                if (ch === '"') {
                    closed = true;
                    i++;
                    break;
                }
                v += ch;
                i++;
            }
            if (!closed)
                return null;
            out.push({ kind: 'quoted', value: v });
        }
        else {
            // Bare token.
            let v = '';
            while (i < raw.length && raw[i] !== ',' && raw[i] !== ' ' && raw[i] !== '\t') {
                v += raw[i];
                i++;
            }
            if (v.length === 0) {
                // Allow consecutive commas (empty positional arg).
                out.push({ kind: 'bare', value: '' });
            }
            else if (!/^[A-Z0-9_.+\-\/]+$/i.test(v)) {
                return null;
            }
            else {
                out.push({ kind: 'bare', value: v });
            }
        }
        skipWs();
        if (i >= raw.length)
            break;
        if (raw[i] === ',') {
            i++;
            // Trailing comma → empty last arg is allowed.
            if (i === raw.length)
                out.push({ kind: 'bare', value: '' });
            continue;
        }
        // Unexpected trailing character.
        return null;
    }
    return out;
}
/** Uppercase outside quoted regions, collapse whitespace outside quotes. */
function normalizeOutsideQuotes(s) {
    let out = '';
    let inQuotes = false;
    let prevWasSpace = false;
    for (let i = 0; i < s.length; i++) {
        const ch = s[i];
        if (inQuotes) {
            if (ch === '\\' && s[i + 1] === '"') {
                out += '\\"';
                i++;
                continue;
            }
            if (ch === '"') {
                inQuotes = false;
                out += ch;
                prevWasSpace = false;
                continue;
            }
            out += ch;
            continue;
        }
        if (ch === '"') {
            inQuotes = true;
            out += ch;
            prevWasSpace = false;
            continue;
        }
        if (ch === ' ' || ch === '\t') {
            if (!prevWasSpace && out.length > 0)
                out += ' ';
            prevWasSpace = true;
            continue;
        }
        out += ch.toUpperCase();
        prevWasSpace = false;
    }
    return out.trim();
}
/** Split a raw line on semicolons that are outside quoted regions. */
export function splitSemicolonChain(raw) {
    const parts = [];
    let buf = '';
    let inQuotes = false;
    for (let i = 0; i < raw.length; i++) {
        const ch = raw[i];
        if (inQuotes) {
            if (ch === '\\' && raw[i + 1] === '"') {
                buf += '\\"';
                i++;
                continue;
            }
            if (ch === '"')
                inQuotes = false;
            buf += ch;
            continue;
        }
        if (ch === '"') {
            inQuotes = true;
            buf += ch;
            continue;
        }
        if (ch === ';') {
            parts.push(buf);
            buf = '';
            continue;
        }
        buf += ch;
    }
    if (buf.length > 0)
        parts.push(buf);
    // Sub-commands after the first typically lack `AT` prefix (firmware-dependent);
    // prepend `AT` when missing so parseAtCommand can handle them uniformly.
    return parts.map((p, idx) => {
        const trimmed = p.trim();
        if (idx === 0)
            return trimmed;
        if (trimmed.toUpperCase().startsWith('AT'))
            return trimmed;
        return 'AT' + trimmed;
    });
}
// ─────────────────────────────────────────────────────────────────────────
// Matcher — rules come from the generated atPolicyRules module.
// ─────────────────────────────────────────────────────────────────────────
// Normalize an AT arg for comparison: base-10 ints compare by value so
// zero-padding can't bypass a rule ("01" === "1"); non-numeric falls back to
// lowercase. Kept identical to the Python matcher's _arg_norm (P0-7).
function argNorm(v) {
    const s = (v ?? '').trim();
    return /^-?\d+$/.test(s) ? String(parseInt(s, 10)) : s.toLowerCase();
}
function ruleMatches(cmd, rule) {
    if (rule.prefix !== undefined && rule.prefix !== cmd.prefix)
        return false;
    if (rule.name !== undefined && rule.name.toUpperCase() !== cmd.name.toUpperCase())
        return false;
    if (rule.operator !== undefined && rule.operator !== cmd.operator)
        return false;
    if (rule.min_args !== undefined && cmd.args.length < rule.min_args)
        return false;
    if (rule.args_equal !== undefined) {
        if (cmd.args.length !== rule.args_equal.length)
            return false;
        for (let i = 0; i < rule.args_equal.length; i++) {
            if (argNorm(cmd.args[i].value) !== argNorm(rule.args_equal[i]))
                return false;
        }
    }
    if (rule.first_arg_equal !== undefined) {
        if (cmd.args.length === 0)
            return false;
        if (argNorm(cmd.args[0].value) !== argNorm(rule.first_arg_equal))
            return false;
    }
    return true;
}
function classifyOne(cmd) {
    for (const r of EXEMPT_RULES) {
        if (ruleMatches(cmd, r))
            return { severity: 'exempt', reason: null };
    }
    for (const r of BLOCKED_RULES) {
        if (ruleMatches(cmd, r))
            return { severity: 'blocked', reason: r.reason };
    }
    return { severity: 'pass', reason: null };
}
/**
 * Evaluate a raw AT command line (possibly semicolon-chained) against the
 * policy. Returns the action the caller must take.
 *
 * Behavior:
 *   - Splits on semicolons outside quoted regions.
 *   - Parses each sub-command independently; unparseable → BLOCK.
 *   - If ANY sub-command is BLOCKED, the whole line is BLOCKED.
 *   - If ALL sub-commands are EXEMPT, the whole line is EXEMPT.
 *   - Otherwise PASS.
 *
 * Callers:
 *   - Server (ATCommandExecutor + Quick Command REST) — callers may honor
 *     a primitive-scoped `allowBlockedCommands` allowlist; see plan.
 *   - Edge (modem-gateway central_client handler) — no allowlist honored.
 */
export function guardAtCommand(raw) {
    const subs = splitSemicolonChain(raw);
    if (subs.length === 0) {
        return { action: 'block', severity: 'blocked', blockReason: 'Empty command', offendingSubcommand: null };
    }
    let allExempt = true;
    for (const sub of subs) {
        const parsed = parseAtCommand(sub);
        if (parsed === null) {
            return {
                action: 'block',
                severity: 'blocked',
                blockReason: 'Unparseable command — fail-closed. Add the form to the grammar via a reviewed code change, or use SSH + direct serial.',
                offendingSubcommand: sub,
            };
        }
        const { severity, reason } = classifyOne(parsed);
        if (severity === 'blocked') {
            return {
                action: 'block',
                severity: 'blocked',
                blockReason: reason,
                offendingSubcommand: subs.length === 1 ? null : sub,
            };
        }
        if (severity !== 'exempt')
            allExempt = false;
    }
    return {
        action: 'pass',
        severity: allExempt ? 'exempt' : 'pass',
        blockReason: null,
        offendingSubcommand: null,
    };
}
/**
 * Server-only convenience: evaluate with a caller-provided allowlist.
 * Used by ATCommandExecutor when the command originates from a reviewed
 * primitive that carries `allowBlockedCommands`. The allowlist is
 * exact-match on the normalized raw command (uppercase AT prefix, trimmed).
 *
 * The Quick Command REST route must NOT pass an allowlist — free-form
 * console never gets an override.
 */
export function guardAtCommandWithAllowlist(raw, allowlist) {
    const decision = guardAtCommand(raw);
    if (decision.action !== 'block')
        return decision;
    if (!allowlist || allowlist.length === 0)
        return decision;
    const normalized = raw.replace(/[\r\n]+/g, '').trim().toUpperCase();
    for (const entry of allowlist) {
        if (entry.replace(/[\r\n]+/g, '').trim().toUpperCase() === normalized) {
            return { action: 'pass', severity: 'pass', blockReason: null, offendingSubcommand: null };
        }
    }
    return decision;
}
